import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Flex,
  Icon,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kognitos/lattice';
import { ABOVE_OUTPUT_MODAL_CLASSNAME } from '@/shared/constants/output-modal-layer';

interface IOutputModalHeaderProps {
  title: string;
  typeLabel: string;
  onCopy?: () => void;
  onDownload?: () => void;
  onClose: () => void;
  metadata?: ReactNode;
  titleId: string;
  viewControl?: ReactNode;
  showCloseButton?: boolean;
}

export function OutputModalHeader({
  title,
  typeLabel,
  onCopy,
  onDownload,
  onClose,
  metadata,
  titleId,
  viewControl,
  showCloseButton = true,
}: IOutputModalHeaderProps) {
  const { t } = useTranslation('outputs');
  const copyLabel = t('outputs.card.copy');
  const downloadLabel = t('outputs.actions.download');
  const closeLabel = t('common.action.close', { ns: 'common' });

  return (
    <Flex
      align="center"
      justify="between"
      gap={3}
      className="output-modal-header min-h-8"
    >
      <Flex align="center" gap={2} className="min-w-0 flex-1">
        <Text id={titleId} weight="medium" className="truncate">
          {title}
        </Text>
        <Badge variant="outline" className="shrink-0">
          {typeLabel}
        </Badge>
        {metadata}
      </Flex>

      <Flex align="center" gap={1} className="shrink-0">
        {viewControl}
        {onCopy && (
          <Tooltip>
            {/* Focus alone (e.g. this button regaining it after an arrow-key
                page) must not pop the tooltip open; the button's own
                aria-label already carries the name to keyboard/AT users. */}
            <TooltipTrigger asChild onFocus={event => event.preventDefault()}>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={onCopy}
                aria-label={copyLabel}
              >
                <Icon type="Copy" size="sm" />
              </Button>
            </TooltipTrigger>
            {/* Lattice's TooltipContent ships z-50; the modal backdrop is z-100
                and would otherwise sit above it. */}
            <TooltipContent className={ABOVE_OUTPUT_MODAL_CLASSNAME}>
              {copyLabel}
            </TooltipContent>
          </Tooltip>
        )}
        {onDownload && (
          <Tooltip>
            <TooltipTrigger asChild onFocus={event => event.preventDefault()}>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={onDownload}
                aria-label={downloadLabel}
              >
                <Icon type="Download" size="sm" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className={ABOVE_OUTPUT_MODAL_CLASSNAME}>
              {downloadLabel}
            </TooltipContent>
          </Tooltip>
        )}
        {showCloseButton && (
          <Tooltip>
            <TooltipTrigger asChild onFocus={event => event.preventDefault()}>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={onClose}
                aria-label={closeLabel}
                data-output-modal-autofocus
              >
                <Icon type="X" size="sm" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className={ABOVE_OUTPUT_MODAL_CLASSNAME}>
              {closeLabel}
            </TooltipContent>
          </Tooltip>
        )}
      </Flex>
    </Flex>
  );
}
